// ----------------------------------------------------------------------
// <copyright file="Izenda.LinearGauge.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

!(function() {
  const { get, isNumber, stop, tail, size, getOr, isNil, isEmpty } = require('lodash/fp');
  module.exports = function(H) {
    var CHART_SIZE_RATIO = 7;
    var PADDING = 10;
    var WIDTH = 500;
    var HEIGHT = 130;
    var UNIT_LENGTH = 20;

    var TICK_LENGTH = 3;
    var TICK_WIDTH = 1;
    var TICK_COLOR = '#fff';
    var OTHER_COLOR = '#ccc';
    var MAKER_COLOR = '#000';
    var MAKER_MIN_SIZE = 10;

    var BASE_CHART = 'column';
    var IZENDA_LINEAR_GAUGE = 'izendaLinearGauge';

    var defaultPlotOptions = H.getOptions().plotOptions,
      baseChart = H.seriesTypes[BASE_CHART],
      wrap = H.wrap,
      each = H.each;

    defaultPlotOptions[IZENDA_LINEAR_GAUGE] = H.merge(defaultPlotOptions[BASE_CHART], {});

    H.seriesTypes[IZENDA_LINEAR_GAUGE] = H.extendClass(baseChart, {
      type: IZENDA_LINEAR_GAUGE,

      drawPoints: function() {
        var series = this,
          chart = this.chart,
          chartOptions = chart.options,
          chartType = chartOptions.chart.type,
          renderer = chart.renderer,
          makerSize = MAKER_MIN_SIZE,
          shapeArgs;

        if (chartType !== IZENDA_LINEAR_GAUGE) {
          return baseChart.prototype.drawPoints.apply(this, arguments);
        }

        // draw maker instead
        each(series.points, function(point) {
          var plotY = point.plotY,
            graphic = point.graphic;
          var markerY;

          if (isNumber(plotY) && point.y !== null) {
            shapeArgs = point.shapeArgs;

            markerY = -Math.min(
              chart.plotBox[get('chart.inverted', chartOptions) ? 'width' : 'height'],
              shapeArgs.height
            );

            if (graphic) {
              stop(graphic);
              // move to new position
              graphic.animate({
                translateX: makerSize / 3,
                translateY: markerY
              });
            } else {
              var startPointX = 0;
              var startPointY = point.plotY + shapeArgs.height;
              var baseX = startPointX - makerSize;
              var path = [
                'M',
                startPointX,
                startPointY,
                'L',
                baseX,
                startPointY + makerSize / 2,
                baseX,
                startPointY - makerSize / 2
              ];

              point.graphic = renderer
                .path(path)
                .attr({
                  fill: MAKER_COLOR,
                  stroke: MAKER_COLOR
                })
                .add(series.markerGroup)
                .animate({
                  translateX: makerSize / 3,
                  translateY: markerY
                });
            }
          } else if (graphic) {
            point.graphic = graphic.destroy();
          }
        });
      },

      alignDataLabel: function(point, dataLabel, options, alignTo, isNew) {
        var pathWidth = getPathLengthByValue(this.chart, point.y);
        dataLabel = point.dataLabel;
        var plotSizeX = this.chart.plotSizeX;
        var plotSizeY = this.chart.plotSizeY;

        var dataLabelHeight = dataLabel.height + dataLabel.padding;
        var dataLabelWidth = dataLabel.width + dataLabel.padding;

        if (this.chart.options.chart.inverted) {
          alignTo = {
            x: plotSizeY - point.plotY - dataLabelWidth / 2,
            y: plotSizeX - pathWidth - dataLabel.height
          };

          if (alignTo.x + dataLabelWidth > plotSizeY) {
            alignTo.x = plotSizeY - dataLabelWidth;
          } else if (alignTo.x < 0) {
            alignTo.x = 0;
          }
        } else {
          alignTo = {
            x: pathWidth,
            y: Math.max(point.plotY - dataLabelHeight / 2, -dataLabelHeight / 2)
          };

          alignTo.height = plotSizeY - alignTo.y - 1;
        }

        H.Series.prototype.alignDataLabel.call(this, point, dataLabel, options, alignTo, isNew);
      }
    });

    /** override AXIS function **/
    wrap(H.Axis.prototype, 'getPlotLinePath', function(proceed, value) {
      var options = this.chart.options;
      var chartType = options.chart.type;
      var args = tail(arguments);
      var pathLine = proceed.apply(this, args);

      if (chartType === IZENDA_LINEAR_GAUGE) {
        return getLinearPath.call(this, value, pathLine);
      } else {
        return pathLine;
      }

      function getLinearPath(value, path) {
        var chart = this.chart;
        var pathLength = getPathLengthByValue(chart, value);

        if (this.horiz) {
          path[2] = path[5] - pathLength;
        } else {
          path[4] = path[1] + pathLength;
        }
        return path;
      }
    });

    function getPathLengthByValue(chart, value) {
      var izendaOptions = chart.options.izendaOptions;
      var valueRange = izendaOptions.scaleTo - izendaOptions.scaleFrom;
      var valueLength = getValue();
      var valueRatio = valueLength / valueRange;

      return Math.max(getBasedSize(), MAKER_MIN_SIZE) * valueRatio;

      function getBasedSize() {
        return Math.min(chart.plotSizeY / CHART_SIZE_RATIO, chart.plotSizeX);
      }

      function getValue() {
        if (value < izendaOptions.scaleFrom) {
          return 0;
        } else if (value > izendaOptions.scaleTo) {
          return valueRange;
        } else {
          return value - izendaOptions.scaleFrom;
        }
      }
    }

    /** OVERRIDE HIGHCHART FUNCTION **/
    wrap(H.Chart.prototype, 'init', function(proceed, userOptions, callback) {
      var izendaOptions = userOptions.izendaOptions || {};
      var isInverted = get('chart.inverted', userOptions);
      var chartType = userOptions.chart.type;

      if (chartType === IZENDA_LINEAR_GAUGE) {
        userOptions = H.merge(userOptions, {
          chart: getChartSize(isInverted),

          title: getTitleConfig(izendaOptions.showLabels, userOptions.title, isInverted),

          yAxis: {
            title: {
              enabled: true,
              text: getUnitText(izendaOptions.dataUnit),
              x: isInverted ? 0 : -5,
              margin: 0
            },
            plotBands: updatePlotBands(userOptions),
            labels: {
              style: {
                fontSize: '14px'
              }
            }
          },
          xAxis: {
            lineWidth: 0,
            tickLength: 0,
            labels: {
              enabled: false
            }
          }
        });
      }

      proceed.apply(this, [userOptions, callback]);

      function getUnitText(unitText) {
        unitText = unitText || '';
        return size(unitText) > UNIT_LENGTH ? unitText.substring(0, UNIT_LENGTH) + '...' : unitText;
      }

      function updatePlotBands(chartOptions) {
        var yAxis = chartOptions.yAxis || {};
        var plotBands = yAxis.plotBands;
        var min, max, value;

        if (!plotBands || !plotBands.length) {
          min = yAxis.min;
          max = yAxis.max;
          value = getOr(max + min / 2, 'series[0].data[0][y]', chartOptions);

          const color = isNil(chartOptions.colors)
            ? H.getOptions().colors[0]
            : isEmpty(chartOptions.colors)
            ? MAKER_COLOR
            : chartOptions.colors[0];
          return [
            {
              from: min,
              to: value,
              color: color
            },
            {
              from: value,
              to: max,
              color: OTHER_COLOR
            }
          ];
        }

        return plotBands;
      }

      function getTitleConfig(isShow, titleConfig, isInverted) {
        if (!isShow) {
          return $izenda.extend(titleConfig, {
            text: ''
          });
        }

        return $izenda.extend(titleConfig, {
          align: isInverted ? 'center' : 'left',
          verticalAlign: 'top',
          useHTML: true,
          text: String.format(
            '<span style="width:{0}" class="overflow-text" title="{1}">{1}</span>',
            (isInverted ? WIDTH : HEIGHT) * 0.7,
            izendaOptions.serieName
          )
        });
      }

      function getChartSize(isInverted) {
        if (isInverted) {
          return {
            height: HEIGHT,
            width: WIDTH,
            margin: [PADDING * 2, PADDING, PADDING * 3, PADDING]
          };
        } else {
          return {
            height: WIDTH,
            width: HEIGHT + PADDING * 3,
            margin: [PADDING * 2, PADDING * 3, PADDING, PADDING * 4]
          };
        }
      }
    });

    wrap(H.Chart.prototype, 'getAxes', function(proceed) {
      var options = this.options;
      var izendaOptions = options.izendaOptions || {};
      var chartType = options.chart.type;
      var isInverted = get('chart.inverted', options);

      if (chartType === IZENDA_LINEAR_GAUGE) {
        this.options.xAxis = getXaxis(izendaOptions);
        this.options.yAxis = H.merge(this.options.yAxis, getYaxis(izendaOptions, this.options.yAxis, isInverted));
      }

      proceed.apply(this, tail(arguments));

      function getYaxis(izendaOptions, userOptionsYAxis, isInverted) {
        izendaOptions = izendaOptions || {};

        var min = izendaOptions.scaleFrom;
        var max = izendaOptions.scaleTo;
        var yAxis = {
          tickLength: TICK_LENGTH,
          tickWidth: TICK_WIDTH,
          tickColor: TICK_COLOR,
          gridLineColor: TICK_COLOR,
          gridLineWidth: TICK_WIDTH,
          minorTickInterval: 0,
          minorTickWidth: 0,
          minorGridLineWidth: 0,
          tickPositioner: getTickPostions
        };

        !isInverted && attachLabelFormatter(yAxis, userOptionsYAxis);

        return yAxis;

        function attachLabelFormatter(yAxis, userOptionsYAxis) {
          var oldFormatter = get('labels.formatter', userOptionsYAxis);
          yAxis.labels = {
            useHTML: true,
            formatter: function() {
              return String.format(
                '<span class="overflow-text" style="width:{0}px"><span title="{1}">{1}</span></span>',
                PADDING * 2,
                oldFormatter.apply(this, arguments)
              );
            }
          };
        }

        function getTickPostions() {
          var axisLength = this.len;

          return isNumber(izendaOptions.intervals) ? getPositions(izendaOptions.intervals) : [min, max];

          function getPositions(interval) {
            var positions = [];
            var pos = min;
            var numTicks = Math.floor((max - min) / interval);
            var tickDistancePx = Math.floor(numTicks && axisLength / numTicks);

            if (interval < 0 || !numTicks || !tickDistancePx) {
              return [min, max];
            }

            if (tickDistancePx < 50) {
              return getPositions(Math.min(interval * 2, max - min));
            }

            while (pos + interval <= max) {
              positions.push(pos);
              pos += interval;
            }

            positions.push(max);

            return positions;
          }
        }
      }

      function getXaxis(userOptions) {
        return {
          lineWidth: 0,
          tickLength: 0,
          labels: {
            enabled: false
          }
        };
      }
    });
  };
})();



// WEBPACK FOOTER //
// ./ui/share/components/charting/highcharts/Izenda.LinearGauge.js